import React from "react";

import _ from "lodash";
import moment from "moment";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";
import { makeAutoObservable } from "mobx";
import {
  Area,
  AreaChart,
  Line,
  LineChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceArea,
} from 'recharts';

import { ReactComponent as CloseIcon } from "@src/assets/icons/close.svg";
import useVM from "@src/hooks/useVM";
import useIsMobile, { MOBILE_BREAKPOINT } from "@src/hooks/useIsMobile";
import { useConvert, useLang, useSpeed, useWeight, useYield } from "@src/hooks/useLang";
import { computePointYields, isJohnDeereGates, type PointYield, type YieldGates } from "./panelYield";
import { WeightUnit } from "@services/demosService";
import { DeviceStatisticsResponse, DeviceType, type TDeviceSource } from "@services/devicesService";
import { downsampleRows, sliceToWindow } from "@src/utils/chartDownsample";
import { precision } from "@geobuf/precision";
import {
  sensorColor, speedColor, normalizeSensor,
  cameraChartValues, moistureColor, moistureScale, fuelColor, fuelScale,
  SENSOR_PALLET, MOISTURE_PALLET, FUEL_PALLET,
  POTATO_LAYER, NOT_POTATO_LAYER, SIZE_LAYER, MOISTURE_LAYER, FUEL_LAYER, SPEED_LAYER,
} from "@screens/TracksScreen/utils";
import { LayerLegend } from "@screens/TracksScreen/ValueLegend";
import Text from "@components/Text";
import Button from "@components/Button";
import { ROUTES } from "@stores/AccountStore";

const CHART_HEIGHT = 180;
const MAX_DURATION_BETWEEN_POINTS = 5 * 60;

type ViewMode = "normalized" | "yield";

// Yield: the gates, coefficients and widths come from the server per point
// (GET /devices/:id/tracks?yield=1); panelYield.ts only applies the band and
// sums. See there for why.

// One device's data for the detail plot. The plot shows the full yield /
// calibration tooling when a single device is selected, and a synced overlay
// of several devices when more than one is selected.
export type TrackInput = {
  id: string;
  name: string;
  color: string;
  statistics: DeviceStatisticsResponse;
  deviceType: DeviceType;
  /** Undefined = greengrowth. A John Deere ribbon is colored by relative t/ha. */
  source?: TDeviceSource;
};

/** t/ha as the c/ha that convertYield expects. */
const TONS_PER_HA_TO_CENTNERS = 10;

/**
 * Strings added for John Deere combines. Read through a plain record so a
 * locale that does not carry them yet falls back to the English text.
 */
const jdStrings = (lang: ReturnType<typeof useLang>) => {
  const tracks = lang.tracks as Record<string, string | undefined>;
  const tracksYield = lang.tracksYield as Record<string, string | undefined>;
  return {
    /** The sensor layer / value of a Deere combine: t/ha on a relative scale. */
    yieldRelative: tracks.yieldRelativeLayerTitle ?? "Yield (relative)",
    coefficientJohnDeere:
      tracksYield.coefficientJohnDeere ?? "From John Deere (no GreenGrowth calibration yet)",
  };
};

////////////////////////////////////////////////////////////////////////////////
// CONTEXT
////////////////////////////////////////////////////////////////////////////////

const CONTEXT = React.createContext<Context | null>(null);

class Context {
  data: any[] = [];
  on_zoom: ZoomCallback;

  left: number;
  right: number;

  // Edges of the reference area
  ref_area_left?: number;
  ref_area_right?: number;

  // Yield view state
  viewMode: ViewMode = "normalized";
  /** The server's speed band / static flag; null when yield was not requested. */
  gates: YieldGates | null;
  // Values typed into the panel replace the calibration / the band maximum for
  // this session only (never persisted: the admin setting is the source).
  coefficientOverride: number | null = null;
  maxSpeedOverride: number | null = null;
  /** Attachment id, for "Use for calibration". */
  deviceId: string;
  /** A John Deere combine: its value is relative t/ha, and no GreenGrowth gate applies. */
  johnDeere: boolean;
  /** Tells the screen about a typed coefficient, so its export uses it too. */
  on_coefficient: CoefficientCallback;

  constructor(
    data: any[],
    on_zoom: ZoomCallback,
    gates: YieldGates | null,
    deviceId: string,
    johnDeere: boolean,
    on_coefficient: CoefficientCallback,
  ) {
    this.data = data;
    this.left = 0;
    this.right = data.length - 1;
    this.on_zoom = on_zoom;
    this.gates = gates;
    this.deviceId = deviceId;
    this.johnDeere = johnDeere || isJohnDeereGates(gates);
    this.on_coefficient = on_coefficient;
    makeAutoObservable(this, { data: false, on_coefficient: false });
  }

  /** km/h: the typed override, else the device's admin band maximum. */
  get maxSpeed(): number {
    return this.maxSpeedOverride ?? this.gates?.maxSpeed ?? 0;
  }

  /**
   * The zoomed window. A computed, so it keeps its identity until the zoom
   * moves: the charts memoise their downsampling on it, and a fresh slice on
   * every render (each pointer move during a drag) redid it every time.
   */
  get visibleData(): any[] {
    if (_.isNull(this.left) || _.isNull(this.right)) {
      return this.data;
    }
    return this.data.slice(this.left, this.right);
  }

  getData() {
    return this.visibleData;
  }

  /** Returns visible time range as ISO strings for calibration prefill, or null if invalid */
  getVisibleTimeRange(): { start: string; stop: string } | null {
    if (this.data.length === 0 || this.left == null || this.right == null) return null;
    const startIdx = Math.max(0, this.left);
    const stopIdx = Math.min(this.right, this.data.length - 1);
    if (startIdx >= this.data.length || stopIdx < 0 || startIdx > stopIdx) return null;
    return {
      start: moment.unix(this.data[startIdx].time).toISOString(),
      stop: moment.unix(this.data[stopIdx].time).toISOString(),
    };
  }

  setRefAreaLeft(val: number | undefined) {
    this.ref_area_left = val;
  }

  setRefAreaRight(val: number | undefined) {
    if (!_.isUndefined(this.ref_area_left)) {
      this.ref_area_right = val;
    }
  }

  isZoomApplied(): boolean {
    return this.left > 0 || this.right < this.data.length - 1;
  }

  /**
   * `indices` maps the chart's own point index (the reference area's edges)
   * to an index into `getData()` — a chart draws a downsampled series.
   */
  zoom(indices?: number[]) {
    if (!_.isUndefined(this.ref_area_left) && !_.isUndefined(this.ref_area_right)) {
      let left = indices?.[this.ref_area_left] ?? this.ref_area_left;
      let right = indices?.[this.ref_area_right] ?? this.ref_area_right;
      if (left > right) {
        [left, right] = [right, left];
      }

      this.right = this.left + right;
      this.left += left;

      this.on_zoom(
        moment.unix(this.data[this.left].time).toDate(),
        moment.unix(this.data[this.right].time).toDate()
      );
    } else {
      this.left = 0;
      this.right = this.data.length - 1;
    }

    this.ref_area_left = undefined;
    this.ref_area_right = undefined;
  }

  reset() {
    this.left = 0;
    this.right = this.data.length - 1;
    this.on_zoom();
  }

  setViewMode(mode: ViewMode) {
    this.viewMode = mode;
  }

  setCoefficientOverride(value: number | null) {
    this.coefficientOverride = value;
    this.on_coefficient(value);
  }

  setMaxSpeed(value: number) {
    this.maxSpeedOverride = value;
  }

  /** Coefficient shown in the panel: the override, else the one in force at the end of the visible window. */
  getDisplayedCoefficient(): number {
    if (this.coefficientOverride != null) return this.coefficientOverride;
    const data = this.getData();
    for (let i = data.length - 1; i >= 0; i--) {
      if (data[i].coeff != null) return data[i].coeff;
    }
    return 0;
  }

  /**
   * Mass and covered area per point, aligned with `data`; null = not counted.
   * The server's gates and per-point coefficient / width (panelYield.ts).
   */
  get pointYields(): Array<PointYield | null> {
    if (!this.gates) return new Array(this.data.length).fill(null);
    return computePointYields(this.data, this.gates, {
      coefficientOverride: this.coefficientOverride,
      maxSpeedOverride: this.maxSpeedOverride,
      johnDeere: this.johnDeere,
    });
  }

  /** `pointYields` for the visible window, aligned with `getData()`. */
  getVisiblePointYields(): Array<PointYield | null> {
    const all = this.pointYields;
    if (_.isNull(this.left) || _.isNull(this.right)) return all;
    return all.slice(this.left, this.right);
  }

  // Compute total harvested tons for visible data
  getTotalTons(): number {
    let totalKg = 0;
    for (const p of this.getVisiblePointYields()) {
      if (p) totalKg += p.kg;
    }
    return totalKg / 1000;
  }

  /** Total mass / total area over the visible window, t/ha. Null when no area. */
  getAverageYield(): number | null {
    let kg = 0;
    let areaM2 = 0;
    for (const p of this.getVisiblePointYields()) {
      if (!p) continue;
      kg += p.kg;
      areaM2 += p.areaM2;
    }
    return areaM2 > 0 ? (kg / areaM2) * 10 : null;
  }
}

type ZoomCallback = (from?: Date, to?: Date) => void;
/** The panel's typed coefficient changed; null = back to the calibration. */
type CoefficientCallback = (value: number | null) => void;
/**
 * A coefficient typed into the Details panel, with the loaded day it was
 * typed for: the panel starts over on a new day, so the screen's export must
 * ignore a value whose `statistics` are not the track's current ones.
 */
export type CoefficientOverrideCallback = (
  deviceId: string,
  statistics: DeviceStatisticsResponse,
  value: number | null,
) => void;

// Contenxt provider for the the tracks screen state.
const ContextProvider: React.FC<{
  data: any[];
  onZoom: ZoomCallback;
  gates: YieldGates | null;
  deviceId: string;
  johnDeere: boolean;
  onCoefficient: CoefficientCallback;
}> = ({ data, onZoom, gates, deviceId, johnDeere, onCoefficient, children }) => {
  // The parent re-renders on every pointer move over a map marker. Building a
  // new Context each render reset the zoom, the yield/sensor mode and a typed
  // coefficient; keep one per dataset instead. onZoom is a fresh closure every
  // render, so the Context calls the latest one through a ref.
  const onZoomRef = React.useRef(onZoom);
  onZoomRef.current = onZoom;
  const onCoefficientRef = React.useRef(onCoefficient);
  onCoefficientRef.current = onCoefficient;
  const context = React.useMemo(
    () =>
      new Context(
        data,
        (from, to) => onZoomRef.current(from, to),
        gates,
        deviceId,
        johnDeere,
        (value) => onCoefficientRef.current(value),
      ),
    [data, gates, deviceId, johnDeere]
  );
  return <CONTEXT.Provider value={context}>{children}</CONTEXT.Provider>;
};

export const useContext = () => useVM(CONTEXT);

/**
 * Drag-to-zoom handlers for the single-device charts. Off on phones: a drag
 * competes with scrolling the sheet and wins (bug-109, same rule as
 * components/DeviceMetrics/useChartZoom). Phones keep the day picker and the
 * zoom-reset button.
 *
 * `indices`: the chart's point index → index into `ctx.getData()`, for a chart
 * drawing a downsampled series (downsampleRows).
 */
const useDragZoom = (ctx: Context, indices?: number[]) => {
  const isMobile = useIsMobile();
  if (isMobile) return {};
  return {
    onMouseDown: (e: { activeTooltipIndex?: number }) => ctx.setRefAreaLeft(e?.activeTooltipIndex),
    onMouseMove: (e: { activeTooltipIndex?: number }) => ctx.setRefAreaRight(e?.activeTooltipIndex),
    onMouseUp: () => ctx.zoom(indices),
  };
};

////////////////////////////////////////////////////////////////////////////////
// STYLES
////////////////////////////////////////////////////////////////////////////////

const StyleWindow = styled.div<{ opened: boolean }>`
  position: absolute;
  width: 98%;
  left: 1%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  background: rgb(255, 255, 255);
  z-index: 10;
  height: 450px;
  border-radius: 6px 6px 0 0;
  top: calc(100%);

  transition: transform 0.2s ease-out;
  /* Closed peek must be tall enough to fully reveal the header (heading +
     padding + margin ≈ 56px) so "Details" is visible and clickable by
     default; hover lifts it a little higher as an affordance. */
  transform: translate(0, ${({ opened }) => opened ? "-450px" : "-56px"});
  :hover {
    transform: translate(0, ${({ opened }) => opened ? "-450px" : "-88px"});
  }
`;


const StyleHeader = styled.div`
  width: 100%;
  margin-bottom: 12px;
  padding: 5px 16px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const StyleChartContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  padding: 0 16px;
  box-sizing: border-box;
  -webkit-user-select: none; /* Safari */
  -ms-user-select: none; /* IE 10 and IE 11 */
  user-select: none; /* Standard syntax */
`;

const Icon = styled(Text)`
  :hover {
    color: #68ad18;
  }
`;

const StyleToggle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  margin-bottom: 12px;
  border-radius: 4px;
  overflow: hidden;
  border: 1px solid #ccc;
`;

const StyleToggleButton = styled.button<{ active: boolean }>`
  width: 100%;
  padding: 6px 8px;
  border: none;
  cursor: pointer;
  font-size: 12px;
  background: ${({ active }) => active ? "#68ad18" : "#fff"};
  color: ${({ active }) => active ? "#fff" : "#494C5B"};
  transition: background 0.15s, color 0.15s;
  &:hover {
    background: ${({ active }) => active ? "#68ad18" : "#f0f0f0"};
  }
`;

const StyleUseForCalibrationButton = styled(Button)`
  max-width: fit-content;
  height: 28px;
  font-size: 14px;
  line-height: 22px;
  padding: 0 12px;
`;

const StyleLinkButton = styled.button`
  padding: 0;
  margin-top: 4px;
  border: none;
  background: none;
  color: #68ad18;
  font-size: 12px;
  cursor: pointer;
  text-decoration: underline;
`;

/** Up to 6 significant digits, no trailing zeros: 0.025058536 -> "0.0250585". */
function formatCoefficient(value: number): string {
  return String(Number(value.toPrecision(6)));
}

const StyleCalibrationInput = styled.input`
  width: 100%;
  padding: 6px 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 13px;
  box-sizing: border-box;
  margin-top: 4px;
  &:focus {
    outline: none;
    border-color: #68ad18;
  }
`;

const StyleContentRow = styled.div`
  display: flex;
  flex-direction: row;
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    flex-direction: column;
  }
`;

const StyleSettingsPanel = styled.div`
  width: 21%;
  min-width: 140px;
  padding: 1rem 0.75rem;
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    width: 100%;
    min-width: unset;
    padding: 0.5rem 1rem;
  }
`;

const StyleChartsPanel = styled.div`
  width: 79%;
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    width: 100%;
  }
`;

////////////////////////////////////////////////////////////////////////////////
// UTILITY FUNCTIONS
////////////////////////////////////////////////////////////////////////////////

// Remap input value within input range to output range
function remap(
  value: number,
  in_start: number,
  in_end: number,
  out_start: number,
  out_end: number
) {
  return (value - in_start) / (in_end - in_start) * (out_end - out_start) + out_start;
}

// Half-window of the yield chart's rolling average: ±5 points = 11 points.
const YIELD_SMOOTH_HALF = 5;

/**
 * Chart line: total mass / total area over the counted points within
 * ±YIELD_SMOOTH_HALF of each point. Averaging per-point t/ha instead lets one
 * slow step with a tiny area spike the line far above the real yield.
 */
function rollingYield(points: Array<PointYield | null>, i: number): number {
  let kg = 0;
  let areaM2 = 0;
  const lo = Math.max(0, i - YIELD_SMOOTH_HALF);
  const hi = Math.min(points.length - 1, i + YIELD_SMOOTH_HALF);
  for (let j = lo; j <= hi; j++) {
    const p = points[j];
    if (!p) continue;
    kg += p.kg;
    areaM2 += p.areaM2;
  }
  return areaM2 > 0 ? (kg / areaM2) * 10 : 0;
}

// The yield chart's ramp runs from 0 to the highest point drawn, so the line is
// clamped to this percentile of the window's yields (see SensorChart).
const YIELD_CAP_PERCENTILE = 0.98;

/** YIELD_CAP_PERCENTILE of the positive `yield` values; null when there are none. */
function yieldCap(rows: any[]): number | null {
  const values: number[] = [];
  for (const row of rows) {
    if (row.yield > 0) values.push(row.yield);
  }
  if (values.length === 0) return null;
  values.sort((a, b) => a - b);
  return values[Math.floor((values.length - 1) * YIELD_CAP_PERCENTILE)];
}

/** `johnDeereDevice`: the device list says this is an imported John Deere combine. */
function prepareData(statistics: DeviceStatisticsResponse, johnDeereDevice = false): any[] {
  let data: any[] = [];
  if (statistics.getLength() < 2) {
    return data;
  }

  const time = statistics.getTimeList();
  const speed = statistics.getSpeedList();
  const norms = statistics.getNormList();
  const rawSensors = statistics.getSensorList();
  const frequencies = statistics.getFrequencyList();
  const coords = statistics.getCoordsList();
  const { decode } = precision(10);

  // Potato-camera metrics, in their own absolute units, boot dump dropped
  // (same rule as the map).
  const camera = cameraChartValues(statistics);

  // Grain moisture (%). Already null wherever nothing was measured, so the line
  // breaks over those stretches instead of dropping to a fake zero.
  const moistures = statistics.getMoistureList();
  // Fuel sensor input (mV), null where the message carried no reading.
  const fuels = statistics.getFuelMvList();
  // The yield panel's per-point inputs from the server (panelYield.ts).
  const yieldAmounts = statistics.getYieldAmountList();
  const coeffs = statistics.getCoeffList();
  const widths = statistics.getWidthList();
  // Times are whole seconds here, and a John Deere import can log two points in
  // one second. The chart keeps one, but every Deere point counts (trust
  // Deere), so the second one's mass is added to the first.
  const johnDeere = johnDeereDevice || isJohnDeereGates(statistics.getYieldGates());

  // Values used to break the line across a data gap.
  const emptyPoint = {
    sensor: null,
    speed: null,
    rawSensor: null,
    frequency: null,
    timeDiff: null,
    potato: null,
    stone: null,
    size: null,
    moisture: null,
    fuel: null,
    yieldAmount: null,
    coeff: null,
    widthM: null,
  };

  // From the first point: it has no step before it (timeDiff null), which the
  // GreenGrowth gates reject anyway, and a John Deere range counts it.
  for (let i = 0; i < statistics.getLength(); i++) {
    if (i > 0 && time[i] === time[i - 1]) {
      const last = data[data.length - 1];
      if (johnDeere && last && yieldAmounts[i] != null) {
        last.yieldAmount = (last.yieldAmount ?? 0) + (yieldAmounts[i] as number);
      }
      continue;
    }

    if (data.length > 0 && Math.abs(time[i] - data[data.length - 1].time) > MAX_DURATION_BETWEEN_POINTS) {
      data.push({ ...emptyPoint, time: time[i - 1] + 1 });
      data.push({ ...emptyPoint, time: time[i] - 1 });
    }

    data.push({
      time: time[i],
      sensor: Math.floor(norms[i] * 100),
      speed: Math.min(speed[i], 32),
      rawSensor: rawSensors.length > 0 ? rawSensors[i] : null,
      frequency: frequencies.length > 0 ? frequencies[i] : null,
      timeDiff: i > 0 ? time[i] - time[i - 1] : null,
      rawSpeed: speed[i],
      lng: decode(coords[i * 2]),
      lat: decode(coords[i * 2 + 1]),
      potato: camera.potato[i] ?? null,
      stone: camera.stone[i] ?? null,
      size: camera.size[i] ?? null,
      moisture: moistures[i] ?? null,
      fuel: fuels[i] ?? null,
      yieldAmount: yieldAmounts[i] ?? null,
      coeff: coeffs[i] ?? null,
      widthM: widths[i] ?? null,
    });
  }

  return data
}

////////////////////////////////////////////////////////////////////////////////
// COMPONENTS
////////////////////////////////////////////////////////////////////////////////

const SensorChart: React.FC = observer(() => {
  const ctx = useContext();
  const lang = useLang();
  const { convertYield } = useConvert();
  const yieldUnit = useYield()();
  const data = ctx.getData();
  const isYield = ctx.viewMode === "yield";
  // Points are computed in t/ha; shown in the user's yield unit.
  const toUserYield = (tPerHa: number) =>
    convertYield(Math.max(0, tPerHa) * TONS_PER_HA_TO_CENTNERS);

  const dataKey = isYield ? "yield" : "sensor";

  // Yield mode: rolling total mass / total area around each point, over the
  // full series; only what is drawn is downsampled.
  const pointYields = isYield ? ctx.pointYields : null;
  const series = React.useMemo(() => {
    if (!isYield) return data;
    const points = ctx.getVisiblePointYields();
    const rows = data.map((point: any, i: number) => {
      if (point.sensor == null) return point;
      return { ...point, yield: Math.round(rollingYield(points, i) * 100) / 100 };
    });
    // A few slow steps can still spike the line to 10x the field's yield, and
    // the ramp would then paint the real yields in its bottom red band. Spikes
    // are clamped to the axis edge (not dropped: a gap means no harvest); the
    // tooltip keeps the true value.
    const cap = yieldCap(rows);
    if (cap == null) return rows;
    return rows.map((row: any) =>
      row.yield > cap ? { ...row, yield: cap, yieldRaw: row.yield } : row);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isYield, pointYields]);
  const { rows: chartData, indices } = React.useMemo(
    () => downsampleRows(series, dataKey),
    [series, dataKey],
  );
  const dragZoom = useDragZoom(ctx, indices);

  let max = 0;
  for (let i = 0; i < series.length; i++) {
    const val = series[i][dataKey];
    if (val != null) max = Math.max(max, val);
  }

  // Gradient always based on normalized sensor
  const gradientMax = isYield ? 100 : max;
  const gradientLength = Math.round(remap(
    Math.min(gradientMax, 100),
    0, 100, 0, 10
  ));

  return (
    <StyleChartContainer>
      <ResponsiveContainer width="100%" height={CHART_HEIGHT} debounce={100}>
        <AreaChart
          width={500}
          height={500}
          data={chartData}
          syncId="1"
          {...dragZoom}
        >
          <defs>
            <linearGradient id="sensor_fill" x1="0" y1="0" x2="0" y2="1">
              {
                Array.from({ length: gradientLength }, (_, i) => {
                  const from = remap(i, 0, gradientLength, 0, 1);
                  const to = remap(i + 1, 0, gradientLength, 0, 1);
                  const value = normalizeSensor(
                    remap(gradientLength - i, 0, gradientLength, 0, Math.min(gradientMax, 100) / 100));
                  if (i === gradientLength - 1) {
                    return (
                      <React.Fragment key={i}>
                        <stop
                          offset={from}
                          stopColor={sensorColor(value)}
                          stopOpacity={1} />
                      </React.Fragment>
                    );
                  }
                  return (
                    <React.Fragment key={i}>
                      <stop
                        offset={from}
                        stopColor={sensorColor(value)}
                        stopOpacity={1} />
                      <stop
                        offset={to}
                        stopColor={sensorColor(value)}
                        stopOpacity={1} />
                    </React.Fragment>
                  );
                })
              }
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" />
          <YAxis
            yAxisId="sensor_y"
            dataKey={dataKey}
            domain={isYield ? [0, 'auto'] : undefined}
            label={isYield ? { value: yieldUnit, position: 'top', offset: 10 } : undefined}
            tickFormatter={val => isYield ? toUserYield(val).toFixed(1) : Math.round(val).toString()}
          />
          <XAxis
            scale="linear"
            interval="equidistantPreserveStart"
            xAxisId="sensor_x"
            dataKey="time"
            tickFormatter={val => moment.unix(val).format('HH:mm')}
          />
          <Area
            type="monotone"
            yAxisId="sensor_y"
            xAxisId="sensor_x"
            dataKey={dataKey}
            connectNulls={false}
            activeDot={{ r: 3 }}
            stroke="url(#sensor_fill)"
            fill="url(#sensor_fill)"
            fillOpacity={1}
            isAnimationActive={false}
          />
          <Tooltip
            labelFormatter={(label) => {
              return moment.unix(label).format('HH:mm:ss');
            }}
            formatter={(value: number, _name: any, item: any) => {
              if (isYield) {
                const tPerHa = item?.payload?.yieldRaw ?? value;
                return [`${toUserYield(tPerHa).toFixed(2)} ${yieldUnit}`, lang.tracks.yield];
              }
              return [
                Math.round(value),
                ctx.johnDeere ? jdStrings(lang).yieldRelative : lang.tracks.sensorLayerTitle,
              ];
            }}
          />
          {(!_.isUndefined(ctx.ref_area_left) && !_.isUndefined(ctx.ref_area_right)) &&
            <ReferenceArea
              yAxisId="sensor_y"
              xAxisId="sensor_x"
              x1={chartData[ctx.ref_area_left].time}
              x2={chartData[ctx.ref_area_right].time}
              strokeOpacity={0.5} />
          }
        </AreaChart>
      </ResponsiveContainer>
    </StyleChartContainer>
  );
});


// Describes how each non-sensor value layer is charted. `reversed` mirrors the
// map: lots of stones is bad, so that ramp runs green→red instead of red→green.
// `pallet` overrides the shared red→green ramp for a metric that carries its own
// meaning — moisture reads drier→wetter, not bad→good.
// `ramp` is for a metric that lives in a narrow band far from 0 (moisture,
// fuel mV): the map's own smooth ramp over the whole day, and an axis padded
// by `pad` around the data instead of starting at 0.
type LayerMetric = {
  dataKey: "potato" | "stone" | "size" | "moisture" | "fuel";
  langKey:
    | "potatoLayerTitle" | "notPotatoLayerTitle" | "sizeLayerTitle" | "moistureLayerTitle"
    | "fuelLayerTitle";
  fallbackLabel: string;
  unit?: string;
  decimals: number;
  reversed: boolean;
  pallet?: string[];
  ramp?: {
    scale: (values: (number | null)[]) => { lo: number; hi: number };
    color: (value: number, lo: number, hi: number) => string;
    pad: number;
  };
};

const LAYER_METRICS: Record<string, LayerMetric> = {
  [POTATO_LAYER]: {
    dataKey: "potato", langKey: "potatoLayerTitle", fallbackLabel: "Potatoes",
    decimals: 0, reversed: false,
  },
  [NOT_POTATO_LAYER]: {
    dataKey: "stone", langKey: "notPotatoLayerTitle", fallbackLabel: "Not potatoes",
    decimals: 0, reversed: true,
  },
  [SIZE_LAYER]: {
    dataKey: "size", langKey: "sizeLayerTitle", fallbackLabel: "Average size",
    unit: "px", decimals: 1, reversed: false,
  },
  [MOISTURE_LAYER]: {
    dataKey: "moisture", langKey: "moistureLayerTitle", fallbackLabel: "Grain moisture",
    unit: "%", decimals: 1, reversed: false, pallet: MOISTURE_PALLET,
    ramp: { scale: moistureScale, color: moistureColor, pad: 1 },
  },
  [FUEL_LAYER]: {
    dataKey: "fuel", langKey: "fuelLayerTitle", fallbackLabel: "Fuel sensor",
    unit: "mV", decimals: 0, reversed: false, pallet: FUEL_PALLET,
    ramp: { scale: fuelScale, color: fuelColor, pad: 10 },
  },
};

// Charts one of those metrics in its own absolute units, on the same ramp the
// map paints, so the track and the chart can be read against each other.
const MetricChart: React.FC<{ metric: LayerMetric }> = observer(({ metric }) => {
  const lang = useLang();
  const ctx = useContext();
  const visible = ctx.getData();
  const { dataKey, unit, decimals } = metric;
  // Drawn downsampled; the min / max and the ramp below read the full window.
  const { rows: data, indices } = React.useMemo(
    () => downsampleRows(visible, dataKey),
    [visible, dataKey],
  );
  const dragZoom = useDragZoom(ctx, indices);

  const label = (lang.tracks as any)[metric.langKey] ?? metric.fallbackLabel;

  let max = 0;
  let min = Infinity;
  let hasValue = false;
  for (let i = 0; i < visible.length; i++) {
    const v = visible[i][dataKey];
    if (v == null) continue;
    hasValue = true;
    if (v > max) max = v;
    if (v < min) min = v;
  }

  // Grain moisture lives within a few percentage points (fuel mV within a few
  // dozen), so from 0 it would be a flat block. It gets the map's own ramp
  // (stretched over the whole day) and an axis around the data, so a point's
  // color here is its color on the map.
  const ramp = metric.ramp;
  const dayRamp = React.useMemo(
    () => (ramp ? ramp.scale(ctx.data.map((p: any) => p[dataKey])) : null),
    [ctx.data, ramp, dataKey],
  );
  // Nothing to scale in a window with no readings (min is still Infinity).
  const rampRange = hasValue ? dayRamp : null;
  const pad = ramp?.pad ?? 0;
  const yMin = rampRange ? Math.max(0, Math.floor(min) - pad) : 0;
  const yMax = rampRange ? Math.ceil(max) + pad : "auto";

  const pallet =
    metric.pallet ?? (metric.reversed ? [...SENSOR_PALLET].reverse() : SENSOR_PALLET);
  const gradientId = `camera_fill_${dataKey}`;
  // The fill's box runs from the highest point (top) down to the axis floor.
  const stops = rampRange && ramp
    ? Array.from({ length: 9 }, (_, i) => {
      const offset = i / 8;
      const value = max - offset * (max - yMin);
      return { offset, color: ramp.color(value, rampRange.lo, rampRange.hi) };
    })
    : pallet.map((_, i) => ({
      offset: i / (pallet.length - 1),
      color: pallet[pallet.length - 1 - i],
    }));

  if (!hasValue) {
    return (
      <StyleChartContainer>
        <div style={{ height: CHART_HEIGHT, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Text type="text" size={14} color="#899093">
            {label}
          </Text>
        </div>
      </StyleChartContainer>
    );
  }

  return (
    <StyleChartContainer>
      <ResponsiveContainer width="100%" height={CHART_HEIGHT} debounce={100}>
        <AreaChart
          data={data}
          syncId="1"
          {...dragZoom}
        >
          <defs>
            {/* Top of the chart = highest value, so the ramp is emitted in
                reverse to match the SVG's top-down gradient axis. */}
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              {stops.map(({ offset, color }, i) => (
                <stop
                  key={i}
                  offset={offset}
                  stopColor={color}
                  stopOpacity={1}
                />
              ))}
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" />
          <YAxis
            yAxisId="camera_y"
            dataKey={dataKey}
            domain={[yMin, yMax]}
            label={unit ? { value: unit, position: "top", offset: 10 } : undefined}
            tickFormatter={(val) => Number(val).toFixed(decimals)}
          />
          <XAxis
            scale="linear"
            interval="equidistantPreserveStart"
            xAxisId="camera_x"
            dataKey="time"
            tickFormatter={(val) => moment.unix(val).format("HH:mm")}
          />
          <Area
            type="monotone"
            yAxisId="camera_y"
            xAxisId="camera_x"
            dataKey={dataKey}
            connectNulls={false}
            activeDot={{ r: 3 }}
            stroke={`url(#${gradientId})`}
            fill={`url(#${gradientId})`}
            fillOpacity={1}
            isAnimationActive={false}
          />
          <Tooltip
            labelFormatter={(l) => moment.unix(l).format("HH:mm:ss")}
            formatter={(value: number) => [
              unit ? `${value.toFixed(decimals)} ${unit}` : value.toFixed(decimals),
              label,
            ]}
          />
          {(!_.isUndefined(ctx.ref_area_left) && !_.isUndefined(ctx.ref_area_right)) &&
            <ReferenceArea
              yAxisId="camera_y"
              xAxisId="camera_x"
              x1={data[ctx.ref_area_left].time}
              x2={data[ctx.ref_area_right].time}
              strokeOpacity={0.5} />
          }
        </AreaChart>
      </ResponsiveContainer>
    </StyleChartContainer>
  );
});


const SpeedChart: React.FC = observer(() => {
  const lang = useLang();
  const speed = useSpeed();
  const ctx = useContext();
  const visible = ctx.getData();
  const { rows: data, indices } = React.useMemo(() => downsampleRows(visible, "speed"), [visible]);
  const dragZoom = useDragZoom(ctx, indices);

  let max = 0;
  for (let i = 0; i < visible.length; i++) {
    if (visible[i].speed != null) max = Math.max(max, visible[i].speed);
  }

  const length = Math.round(remap(max, 0, 30, 0, 8));

  return (
    <StyleChartContainer>
      <ResponsiveContainer width="100%" height={CHART_HEIGHT} debounce={100}>
        <AreaChart
          width={500}
          height={500}
          data={data}
          syncId="1"
          {...dragZoom}
        >
          <defs>
            <linearGradient id="speed_fill" x1="0" y1="0" x2="0" y2="1">
              {
                Array.from({ length: length }, (_, i) => {
                  const from = remap(i, 0, length, 0, 1);
                  const to = remap(i + 1, 0, length, 0, 1);
                  const value = length - 1 - i;

                  return (
                    <React.Fragment key={i}>
                      <stop
                        offset={from}
                        stopColor={speedColor(value, 7)}
                        stopOpacity={1} />
                      <stop
                        offset={to}
                        stopColor={speedColor(value, 7)}
                        stopOpacity={1} />
                    </React.Fragment>
                  );
                })
              }
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" />
          {/* Points stay in km/h (the ramp is in km/h); ticks read in the
              user's unit, like the legend. */}
          <YAxis
            yAxisId="speed_y"
            dataKey="speed"
            label={{ value: speed.label, position: "top", offset: 10 }}
            tickFormatter={(val) => Math.round(speed.fromKmh(val)).toString()} />
          <XAxis
            scale="linear"
            xAxisId="speed_x"
            interval="equidistantPreserveStart"
            dataKey="time"
            tickFormatter={val => moment.unix(val).format('HH:mm')}
          />
          <Area
            yAxisId="speed_y"
            xAxisId="speed_x"
            connectNulls={false}
            isAnimationActive={false}
            dataKey="speed"
            activeDot={{ r: 3 }}
            stroke="url(#speed_fill)"
            fill="url(#speed_fill)"
            fillOpacity={1}
          />
          <Tooltip
            labelFormatter={(label) => {
              return moment.unix(label).format('HH:mm:ss');
            }}
            formatter={(value: number) => [
              `${speed.fromKmh(value).toFixed(1)} ${speed.label}`,
              lang.tracks.speedLayerTitle,
            ]}
          />
          {(!_.isUndefined(ctx.ref_area_left) && !_.isUndefined(ctx.ref_area_right)) &&
            <ReferenceArea
              yAxisId="speed_y"
              xAxisId="speed_x"
              x1={data[ctx.ref_area_left].time}
              x2={data[ctx.ref_area_right].time}
              strokeOpacity={0.5} />
          }
        </AreaChart>
      </ResponsiveContainer>
    </StyleChartContainer>
  );
});


const Charts: React.FC<{ activeLayer?: string }> = observer(({ activeLayer }) => {
  const ctx = useContext();
  const lang = useLang();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);
  // When a camera layer is on the map, the top chart shows THAT metric in
  // absolute units instead of the normalized sensor value — otherwise the map
  // legend has nothing to be read against.
  const layerMetric = activeLayer ? LAYER_METRICS[activeLayer] : undefined;
  // The panel charts only what the map paints; the sensor's Sensor/Yield
  // tools belong to the sensor layer alone.
  const isSpeedLayer = activeLayer === SPEED_LAYER;
  const isSensorLayer = !layerMetric && !isSpeedLayer;
  const isYield = isSensorLayer && ctx.viewMode === "yield";
  const visibleRange = ctx.getVisibleTimeRange();
  // Min / max of the zoomed window, on the map's own colors.
  const showLegend = isSpeedLayer || activeLayer === MOISTURE_LAYER;
  const visible = showLegend ? ctx.getData() : [];
  // Text being typed into the coefficient box; null shows the coefficient in force.
  const [coeffDraft, setCoeffDraft] = React.useState<string | null>(null);
  const averageYield = isYield ? ctx.getAverageYield() : null;
  const { convertWeight, convertYield } = useConvert();
  const weightUnit = useWeight()();
  const yieldUnit = useYield()();
  const speed = useSpeed();
  const jd = jdStrings(lang);

  const handleUseForCalibration = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!visibleRange) return;
    const params = new URLSearchParams({ start: visibleRange.start, stop: visibleRange.stop });
    navigate(`${ROUTES.DEVICES_CALIBRATION.replace(":id", ctx.deviceId)}?${params.toString()}`);
  };

  return (
    <StyleWindow opened={open} onClick={() => {
      if (!open) {
        setOpen(true);
      }
    }}>
      <StyleHeader>
        <Text
          color="#494C5B"
          type="heading"
          size={24}
          onClick={() => {
            if (open) {
              setOpen(false);
            }
          }}
          style={{ cursor: open ? "pointer" : undefined, flex: 1 }}
        >
          {lang.tracks.bottomSheetHeader}
        </Text>
        {open && (
          <Icon
            textAlign="right"
            size={22}
            onClick={() => {
              setOpen(false);
            }}
            style={{ cursor: "pointer" }}
          >
            <CloseIcon fill="currentColor" stroke="currentColor" />
          </Icon>
        )}
      </StyleHeader>
      <div style={{
        height: "100%",
        overflowY: "auto",
        padding: "0 5px",
        display: open ? "block" : "none",
      }}>
        <StyleContentRow>
          <StyleSettingsPanel>
            {/* Sensor/Yield only describes the sensor chart — meaningless while
                the chart is showing speed, moisture or a camera metric. */}
            {isSensorLayer && (
              <StyleToggle>
                <StyleToggleButton
                  active={ctx.viewMode === "normalized"}
                  onClick={() => ctx.setViewMode("normalized")}
                >
                  {ctx.johnDeere ? jd.yieldRelative : lang.tracks.normalizedSensor}
                </StyleToggleButton>
                <StyleToggleButton
                  active={ctx.viewMode === "yield"}
                  onClick={() => ctx.setViewMode("yield")}
                >
                  {lang.tracks.yield}
                </StyleToggleButton>
              </StyleToggle>
            )}

            {showLegend && activeLayer && (
              <div style={{ marginBottom: "12px" }}>
                <LayerLegend
                  layer={activeLayer}
                  speedsKmh={visible.map((p: any) => p.rawSpeed)}
                  moistures={visible.map((p: any) => p.moisture)}
                  moistureScaleOf={ctx.data.map((p: any) => p.moisture)}
                />
              </div>
            )}

            {visibleRange && (
              <div style={{ marginBottom: "12px" }}>
                <StyleUseForCalibrationButton
                  kind="outline"
                  onClick={handleUseForCalibration}
                >
                  {lang.tracks.useForCalibration ?? "Use for calibration"}
                </StyleUseForCalibrationButton>
              </div>
            )}

            {isYield && (
              <div style={{ marginBottom: "12px" }}>
                <Text type="text" size={12} color="#666">
                  {lang.tracks.calibrationCoefficient}
                </Text>
                <Text type="text" size={10} color="#999">
                  {lang.tracks.calibrationCoefficientHint}
                </Text>
                <StyleCalibrationInput
                  type="number"
                  step="any"
                  min="0"
                  value={coeffDraft ?? formatCoefficient(ctx.getDisplayedCoefficient())}
                  onChange={(e) => {
                    setCoeffDraft(e.target.value);
                    const val = parseFloat(e.target.value);
                    if (!isNaN(val) && val > 0) {
                      ctx.setCoefficientOverride(val);
                    }
                  }}
                  onBlur={() => setCoeffDraft(null)}
                />
                <Text type="text" size={10} color="#999">
                  {ctx.coefficientOverride != null
                    ? lang.tracksYield.coefficientManual
                    : ctx.gates && !ctx.gates.hasCalibration
                      // A Deere combine's default coefficient reproduces
                      // Deere's own mass: not "no calibration".
                      ? (ctx.johnDeere ? jd.coefficientJohnDeere : lang.tracksYield.noCalibration)
                      : lang.tracksYield.coefficientAuto}
                </Text>
                {ctx.coefficientOverride != null && (
                  <StyleLinkButton
                    onClick={() => {
                      setCoeffDraft(null);
                      ctx.setCoefficientOverride(null);
                    }}
                  >
                    {lang.tracksYield.useCalibration}
                  </StyleLinkButton>
                )}
              </div>
            )}

            {/* No speed band applies to a John Deere combine (trust Deere). */}
            {isYield && !ctx.johnDeere && (
              <div style={{ marginBottom: "12px" }}>
                <Text type="text" size={12} color="#666">
                  {`${lang.tracks.maxSpeed} (${speed.label})`}
                </Text>
                <Text type="text" size={10} color="#999">
                  {lang.tracks.maxSpeedTooltip}
                </Text>
                <StyleCalibrationInput
                  type="number"
                  step="0.5"
                  min="0.5"
                  value={Math.round(speed.fromKmh(ctx.maxSpeed) * 10) / 10}
                  onChange={(e) => {
                    const val = parseFloat(e.target.value);
                    if (!isNaN(val) && val > 0) {
                      // Stored and applied in km/h, like every API speed.
                      ctx.setMaxSpeed(speed.toKmh(val));
                    }
                  }}
                />
              </div>
            )}

            {isYield && (
              <div style={{
                marginBottom: "12px",
                padding: "8px",
                background: "#f5f5f5",
                borderRadius: "4px",
              }}>
                <Text type="text" size={12} color="#666">
                  {lang.tracks.totalHarvested}
                </Text>
                <Text type="heading" size={18} color="#494C5B">
                  {`${convertWeight(ctx.getTotalTons(), WeightUnit.Ton).toFixed(2)} ${weightUnit}`}
                </Text>
                <Text type="text" size={12} color="#666">
                  {lang.tracksYield.averageYield}
                </Text>
                <Text type="heading" size={18} color="#494C5B">
                  {averageYield != null
                    ? `${convertYield(averageYield * TONS_PER_HA_TO_CENTNERS).toFixed(1)} ${yieldUnit}`
                    : "—"}
                </Text>
              </div>
            )}

            {!isYield && isSensorLayer && !isMobile && (
              <div style={{ marginBottom: "1rem" }}>
                <Text type="text" size={14} color="#666">
                  {ctx.johnDeere ? jd.yieldRelative : lang.tracks.relativeSensorValue}
                </Text>
              </div>
            )}

            {(ctx.isZoomApplied()) &&
              <Button onClick={() => {
                ctx.reset()
              }}>{lang.tracks.zoomResetButton}</Button>
            }
          </StyleSettingsPanel>
          <StyleChartsPanel>
            {/* One chart: whatever the map is painting. */}
            {isSpeedLayer
              ? <SpeedChart />
              : layerMetric ? <MetricChart metric={layerMetric} /> : <SensorChart />}
          </StyleChartsPanel>
        </StyleContentRow>
      </div>
    </StyleWindow >
  );
});


const SingleWindow: React.FC<{
  statistics: DeviceStatisticsResponse;
  // FIXME(nk2ge5k): I do not like this but it is the only way I can think of
  onZoom: ZoomCallback;
  deviceId: string;
  activeLayer?: string;
  johnDeere: boolean;
  onCoefficientOverride?: CoefficientOverrideCallback;
}> = ({ statistics, onZoom, deviceId, activeLayer, johnDeere, onCoefficientOverride }) => {
  let data = React.useMemo(() => prepareData(statistics, johnDeere), [statistics, johnDeere]);
  return (
    <ContextProvider
      data={data}
      onZoom={onZoom}
      gates={statistics.getYieldGates()}
      deviceId={deviceId}
      johnDeere={johnDeere}
      onCoefficient={(value) => onCoefficientOverride?.(deviceId, statistics, value)}
    >
      <Charts activeLayer={activeLayer} />
    </ContextProvider>
  );
};

////////////////////////////////////////////////////////////////////////////////
// MULTI-DEVICE OVERLAY
////////////////////////////////////////////////////////////////////////////////

const StyleLegendBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 4px 16px 8px;
`;

const StyleChip = styled.button<{ active: boolean; color: string }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border: 1px solid ${({ active, color }) => (active ? color : "rgba(137, 144, 147, 0.4)")};
  border-radius: 14px;
  background: ${({ active }) => (active ? "#fff" : "#f3f3f3")};
  cursor: pointer;
  font-family: Nunito Sans, sans-serif;
  font-size: 13px;
  color: ${({ active }) => (active ? "#494C5B" : "#899093")};
  opacity: ${({ active }) => (active ? 1 : 0.7)};
  &:hover {
    background: #f0f0f0;
  }
`;

const StyleChipDot = styled.span<{ color: string }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${({ color }) => color};
  flex-shrink: 0;
`;

type Series = TrackInput & { data: any[] };

const MultiWindow: React.FC<{
  tracks: TrackInput[];
  onZoom: ZoomCallback;
  activeLayer?: string;
}> = ({ tracks, onZoom, activeLayer }) => {
  const lang = useLang();
  const speed = useSpeed();
  // Drag-to-zoom is pointer-only; on phones it hijacked scrolling (bug-109).
  const isMobile = useIsMobile();
  const [open, setOpen] = React.useState(false);
  // Same rule as SingleWindow: follow the map's active value layer.
  const layerMetric = activeLayer ? LAYER_METRICS[activeLayer] : undefined;
  const isSpeedLayer = activeLayer === SPEED_LAYER;
  // One chart: whatever the map is painting.
  const chartKey = isSpeedLayer ? "speed" : layerMetric ? layerMetric.dataKey : "sensor";
  // Device ids whose series are hidden from the overlay.
  const [hidden, setHidden] = React.useState<Set<string>>(new Set());
  // Zoomed time window ([from, to] unix seconds), or null when showing all.
  const [domain, setDomain] = React.useState<[number, number] | null>(null);
  const [refLeft, setRefLeft] = React.useState<number | undefined>(undefined);
  const [refRight, setRefRight] = React.useState<number | undefined>(undefined);

  const series = React.useMemo<Series[]>(
    () => tracks.map((t) => ({ ...t, data: prepareData(t.statistics, t.source === "johndeere") })),
    [tracks],
  );
  // What each line draws: its series, cut to the zoomed window, downsampled on
  // the charted value. Zoom only narrows the x domain, so cutting first is what
  // brings the detail back. The x axis is numeric time, so a line needs no
  // index mapping back.
  const drawn = React.useMemo<Series[]>(
    () =>
      series.map((s) => {
        const rows = domain ? sliceToWindow(s.data, domain[0], domain[1]) : s.data;
        return { ...s, data: downsampleRows(rows, chartKey).rows };
      }),
    [series, chartKey, domain],
  );

  const [minT, maxT] = React.useMemo(() => {
    let lo = Infinity;
    let hi = -Infinity;
    for (const s of series) {
      for (const p of s.data) {
        if (p.time < lo) lo = p.time;
        if (p.time > hi) hi = p.time;
      }
    }
    if (!isFinite(lo)) return [0, 1];
    return [lo, hi];
  }, [series]);

  const xDomain: [number, number] = domain ?? [minT, maxT];
  const zoomApplied = domain !== null;

  const handleMouseDown = (e: any) => {
    if (e && e.activeLabel != null) setRefLeft(Number(e.activeLabel));
  };
  const handleMouseMove = (e: any) => {
    if (refLeft != null && e && e.activeLabel != null) setRefRight(Number(e.activeLabel));
  };
  const handleMouseUp = () => {
    if (refLeft != null && refRight != null && refLeft !== refRight) {
      let l = refLeft;
      let r = refRight;
      if (l > r) [l, r] = [r, l];
      setDomain([l, r]);
      onZoom(moment.unix(l).toDate(), moment.unix(r).toDate());
    }
    setRefLeft(undefined);
    setRefRight(undefined);
  };
  const reset = () => {
    setDomain(null);
    onZoom();
  };

  const toggleHidden = (id: string) => {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const visibleSeries = drawn.filter((s) => !hidden.has(s.id));

  // One synced line chart per value. Sensor is a fixed 0..100 percentage;
  // speed is km/h shown in the user's unit; camera metrics and moisture are
  // absolute, so they scale to the data.
  //
  // A John Deere combine's sensor value is its relative t/ha, not a sensor
  // reading: it gets its own axis (right of the GreenGrowth one when both are
  // plotted), so the two scales are never read against each other.
  const overlayChart = (key: string) => {
    const isSensor = key === "sensor";
    const isSpeed = key === "speed";
    const unit = isSensor || isSpeed ? undefined : layerMetric?.unit;
    const jdAxisId = "jd_yield_y";
    const isJd = (s: Series) => isSensor && s.source === "johndeere";
    const hasJd = visibleSeries.some(isJd);
    const hasOther = visibleSeries.some((s) => !isJd(s));
    const axisId = hasOther || !hasJd ? `${key}_y` : jdAxisId;
    return (
      <StyleChartContainer>
        <ResponsiveContainer width="100%" height={CHART_HEIGHT} debounce={100}>
          <LineChart
            {...(isMobile ? {} : { onMouseDown: handleMouseDown, onMouseMove: handleMouseMove, onMouseUp: handleMouseUp })}
          >
            <CartesianGrid strokeDasharray="3 3" />
            {(hasOther || !hasJd) && (
              <YAxis
                yAxisId={`${key}_y`}
                domain={isSensor ? [0, 100] : [0, "auto"]}
                unit={unit}
                label={
                  isSpeed
                    ? { value: speed.label, position: "top", offset: 10 }
                    : isSensor && hasJd
                      ? { value: lang.tracks.sensorLayerTitle, position: "top", offset: 10 }
                      : undefined
                }
                tickFormatter={isSpeed ? (val) => Math.round(speed.fromKmh(val)).toString() : undefined}
              />
            )}
            {hasJd && (
              <YAxis
                yAxisId={jdAxisId}
                orientation={hasOther ? "right" : "left"}
                domain={[0, 100]}
                label={{ value: jdStrings(lang).yieldRelative, position: "top", offset: 10 }}
              />
            )}
            <XAxis
              type="number"
              dataKey="time"
              scale="linear"
              domain={xDomain}
              allowDataOverflow
              interval="equidistantPreserveStart"
              tickFormatter={(val) => moment.unix(val).format('HH:mm')}
            />
            {visibleSeries.map((s) => (
              <Line
                key={s.id}
                yAxisId={isJd(s) ? jdAxisId : `${key}_y`}
                type="monotone"
                data={s.data}
                dataKey={key}
                name={s.name}
                stroke={s.color}
                dot={false}
                connectNulls={false}
                isAnimationActive={false}
              />
            ))}
            <Tooltip
              labelFormatter={(label) => moment.unix(Number(label)).format('HH:mm:ss')}
              formatter={isSpeed
                ? (value: number, name: string) => [`${speed.fromKmh(value).toFixed(1)} ${speed.label}`, name]
                : undefined}
            />
            {(!_.isUndefined(refLeft) && !_.isUndefined(refRight)) && (
              <ReferenceArea yAxisId={axisId} x1={refLeft} x2={refRight} strokeOpacity={0.3} />
            )}
          </LineChart>
        </ResponsiveContainer>
      </StyleChartContainer>
    );
  };

  return (
    <StyleWindow opened={open} onClick={() => { if (!open) setOpen(true); }}>
      <StyleHeader>
        <Text
          color="#494C5B"
          type="heading"
          size={24}
          onClick={() => { if (open) setOpen(false); }}
          style={{ cursor: open ? "pointer" : undefined, flex: 1 }}
        >
          {lang.tracks.bottomSheetHeader}
        </Text>
        {open && (
          <Icon
            textAlign="right"
            size={22}
            onClick={() => setOpen(false)}
            style={{ cursor: "pointer" }}
          >
            <CloseIcon fill="currentColor" stroke="currentColor" />
          </Icon>
        )}
      </StyleHeader>
      <div style={{
        height: "100%",
        overflowY: "auto",
        padding: "0 5px",
        display: open ? "block" : "none",
      }}>
        <StyleLegendBar onClick={(e) => e.stopPropagation()}>
          {series.map((s) => (
            <StyleChip
              key={s.id}
              active={!hidden.has(s.id)}
              color={s.color}
              onClick={() => toggleHidden(s.id)}
            >
              <StyleChipDot color={s.color} />
              {s.name}
            </StyleChip>
          ))}
          {zoomApplied && (
            <Button onClick={() => reset()}>{lang.tracks.zoomResetButton}</Button>
          )}
        </StyleLegendBar>

        {overlayChart(chartKey)}
      </div>
    </StyleWindow>
  );
};

const Window: React.FC<{
  tracks: TrackInput[];
  onZoom: ZoomCallback;
  // Value layer currently painted on the map; drives which metric the top
  // chart shows.
  activeLayer?: string;
  /** The Details panel's typed coefficient (one machine only). */
  onCoefficientOverride?: CoefficientOverrideCallback;
}> = ({ tracks, onZoom, activeLayer, onCoefficientOverride }) => {
  if (tracks.length === 0) return null;
  if (tracks.length === 1) {
    const t = tracks[0];
    return (
      <SingleWindow
        statistics={t.statistics}
        onZoom={onZoom}
        deviceId={t.id}
        activeLayer={activeLayer}
        johnDeere={t.source === "johndeere"}
        onCoefficientOverride={onCoefficientOverride}
      />
    );
  }
  return <MultiWindow tracks={tracks} onZoom={onZoom} activeLayer={activeLayer} />;
};

export default Window;
